import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api, TOKEN_KEY } from '../lib/api';

const AuthContext = createContext(null);
const EMPTY_SESSION = {
  token: '',
  user: null,
  permissions: [],
};

function decodeBase64Url(value) {
  if (typeof window === 'undefined' || typeof window.atob !== 'function') {
    return '';
  }

  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = window.atob(padded);

  return decodeURIComponent(
    Array.from(binary, (character) =>
      `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
    ).join('')
  );
}

function readUserFromToken(token) {
  if (!token) {
    return null;
  }

  try {
    const [, payload] = token.split('.');

    if (!payload) {
      return null;
    }

    const parsedPayload = JSON.parse(decodeBase64Url(payload));

    if (!parsedPayload?.username) {
      return null;
    }

    return {
      userId: parsedPayload.userId || '',
      username: parsedPayload.username,
      email: parsedPayload.username,
      expiresAt: parsedPayload.exp
        ? new Date(parsedPayload.exp * 1000).toISOString()
        : null,
    };
  } catch {
    return null;
  }
}

function readStoredSession() {
  if (typeof window === 'undefined') {
    return EMPTY_SESSION;
  }

  try {
    const token = window.localStorage.getItem(TOKEN_KEY) || '';

    if (!token) {
      return EMPTY_SESSION;
    }

    const user = readUserFromToken(token);

    if (!user) {
      window.localStorage.removeItem(TOKEN_KEY);
      return EMPTY_SESSION;
    }

    return {
      token,
      user,
      permissions: [],
    };
  } catch {
    return EMPTY_SESSION;
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => {
    const storedSession = readStoredSession();
    api.setToken(storedSession.token);
    return storedSession;
  });
  const [isReady, setIsReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const authVersion = useRef(0);

  const logout = useCallback((errorMessage = '') => {
    authVersion.current += 1;
    api.logout();
    setSession(EMPTY_SESSION);
    setSessionError(errorMessage);
    setIsReady(true);
  }, []);

  useEffect(() => {
    let active = true;
    const token = session.token;
    const version = authVersion.current;
    if (!token) {
      setIsReady(true);
      return undefined;
    }
    if (token !== api.token) return undefined;
    setIsReady(false);
    let loading = false;
    const refresh = async () => {
      if (loading || !active || version !== authVersion.current || token !== api.token) return;
      loading = true;
      try {
        const data = await api.getSession();
        if (!active || version !== authVersion.current) return;
        if (!data?.user || !Array.isArray(data.permissions)) throw new Error('Invalid session response.');
        setSession((current) => current.token === token ? {
          ...current, user: { ...current.user, ...data.user }, permissions: data.permissions || [],
        } : current);
        setSessionError('');
      } catch (error) {
        if (!active || version !== authVersion.current) return;
        logout(error.status === 401 ? error.message : 'Unable to load your account permissions. Please try again.');
      } finally {
        loading = false;
        if (active && version === authVersion.current) setIsReady(true);
      }
    };
    refresh();
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, [session.token, logout]);

  useEffect(() => {
    // A child logout effect can clear auth before this render's persistence effect runs.
    if (session.token !== api.token) return;

    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (session.token) {
        window.localStorage.setItem(TOKEN_KEY, session.token);
        return;
      }

      window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Storage may be unavailable; the in-memory session still works.
    }
  }, [session.token]);

  useEffect(() => {
    api.setUnauthorizedHandler((error) => logout(error.message));

    return () => {
      api.setUnauthorizedHandler(null);
    };
  }, [logout]);

  const login = async ({ email, password }) => {
    const version = authVersion.current;
    setSessionError('');
    const cleanEmail = email.trim();
    const cleanPassword = password;

    if (!cleanEmail || !cleanPassword) {
      throw new Error('Email and password are required.');
    }

    const data = await api.login(cleanEmail, cleanPassword);
    if (version !== authVersion.current) {
      throw new Error('Sign-in was cancelled. Please try again.');
    }
    api.setToken(data.token);
    const user = readUserFromToken(data.token) || {
      userId: '',
      username: cleanEmail,
      email: cleanEmail,
      expiresAt: null,
    };

    setSession({
      token: data.token,
      user: { ...user, ...data.user },
      permissions: Array.isArray(data.permissions) ? data.permissions : [],
    });

    return data;
  };

  const register = async ({ email, password }) => {
    const cleanEmail = email.trim();
    const cleanPassword = password;

    if (!cleanEmail || !cleanPassword) {
      throw new Error('Email and password are required.');
    }

    return api.register(cleanEmail, cleanPassword);
  };

  return (
    <AuthContext.Provider
      value={{
        token: session.token,
        user: session.user,
        isReady,
        isAuthenticated: Boolean(session.token),
        sessionError,
        can: (permission) => Boolean(session.token) && session.permissions.includes(permission),
        login,
        register,
        verifyEmail: ({ email, code, password }) => api.verifyEmail(email.trim(), code, password),
        resendVerification: ({ email, password }) => api.resendVerification(email.trim(), password),
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.');
  }

  return context;
}
